import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { Line } from 'react-chartjs-2'
import Chart from 'chart.js/auto'
import PropTypes from 'prop-types'
import config from '../../config/config'
import { useTranslation } from 'react-i18next'

/**
 * PopulationHistory
 * Affiche un graphique de l'évolution de la population pour la ville donnée.
 * Utilise une échelle numérique pour refléter les intervalles irréguliers en années.
 * Doit être rendu uniquement quand la page l'encapsulant le décide.
 *
 * @param {object} props
 * @param {object} props.poi - L'objet point of interest contenant au minimum:
 *   - city.name: le nom de la ville (ex: "GEMBRIE")
 *   - city.label: le libellé pour l'affichage
 */
const PopulationHistory = ({ poi }) => {
  const { t } = useTranslation()
  const [populationData, setPopulationData] = useState(null)

  useEffect(() => {
    if (!poi || !poi.city?.name) return

    const fetchPopulation = async () => {
      try {
        const city = encodeURIComponent(poi.city.name)
        const url = `${config.apiUrl}population?city=${city}`
        const response = await axios.get(url)
        const raw = Array.isArray(response.data) ? response.data : []
        const sorted = raw.sort((a, b) => a.year - b.year)
        // Utiliser x numérique (année) pour éviter d'avoir besoin d'un adaptateur de date
        const dataPoints = sorted.map((item) => ({
          x: item.year,
          y: item.value,
        }))
        setPopulationData(dataPoints)
      } catch (error) {
        console.error('Erreur chargement population:', error)
      }
    }

    fetchPopulation()
  }, [poi])

  if (!populationData) return null

  const data = {
    datasets: [
      {
        label: `Population de ${poi.city.label}`,
        data: populationData,
        fill: false,
        tension: 0.3,
      },
    ],
  }

  const options = {
    responsive: true,
    plugins: {
      legend: { display: true, position: 'top' },
      tooltip: { mode: 'nearest', intersect: false },
    },
    scales: {
      x: {
        type: 'linear',
        title: { display: true, text: t('YEAR') },
        ticks: { precision: 0 },
      },
      y: {
        title: { display: true, text: t('POPULATION') },
        beginAtZero: true,
      },
    },
  }

  return (
    <div>
      <hr />
      <h2>{t('POPULATION_GROWTH')}</h2>
      <div className="common-box">
        <Line data={data} options={options} />
      </div>
    </div>
  )
}

PopulationHistory.propTypes = {
  poi: PropTypes.shape({
    city: PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string,
    }).isRequired,
  }).isRequired,
}

export default PopulationHistory
